import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { cloneDeep, isEmpty } from "lodash";
import {
  swatch,
  downloadIcon,
  colouredAddCircle,
} from "../../assets/components/svgs";
import InputField from "../shared/Fields/InputField";
import { getLanguageFileName, isValidLocale, getLanguageName } from "./Util";
import Clipboard from "../../assets/clipboard.svg";
import Dropzone from "../shared/DragAndDrop/Dropzone";
import { IconButton } from "../primitives/buttons/IconButton";
import BiButton from "../primitives/buttons/BiButton.primitive";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";
import PickList, { PICKLIST_TYPES } from "../shared/Fields/PickList";
import { AppConstants } from "../../constants/AppConstants";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setToastData } from "../../app/toastReducer";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import Card from "../shared/cards/Card";
import { listFiles } from "../../http/asset-management-services";
import { HTTP_STATUS } from "../../http/constants/http.status";
import { LanguageObject, defaultLanguageSchema } from "./Policies";
import FileCard from "./FileCard";

interface RulesProps {
  title: string;
  pickerTitle: string;
  addButtonTitle: string;
  data: LanguageObject;
  setData: Function;
  fileKey: string;
}

interface Values {
  key:
    | "file"
    | "languageName"
    | "languageNameError"
    | "localeNameError"
    | "localeName"
    | "createMode"
    | "listOptions"
    | "currentOption";
  value: any;
}

const UploadLanguageCard = forwardRef(
  (
    { title, pickerTitle, addButtonTitle, data, setData, fileKey }: RulesProps,
    ref: any
  ) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const dataSetter = (values: Array<Values>) => {
      let obj: any = {};
      values.forEach(({ key, value }) => {
        obj = { ...obj, [key]: value };
      });
      setData({ ...data, ...obj });
    };

    const {
      file,
      languageName,
      languageNameError,
      localeNameError,
      localeName,
      createMode,
      listOptions,
      currentOption,
    } = data;

    const getLanguageNameViaOptions = (currentOption: Array<any>) => {
      if (currentOption[0]) {
        const { metadata = {} } = currentOption[0];
        const { filename = {}, languagename = "" } = metadata;
        return languagename || filename;
      }
    };

    useEffect(() => {
      if (createMode && isValidLocale(localeName) && !isEmpty(file)) {
        const fileValue = file[Object.keys(file)[0]];
        dataSetter([
          {
            key: "file",
            value: {
              [fileKey]: fileValue,
            },
          },
        ]);
      }
    }, [localeName]);

    const handleUpload = () => {
      ref.current.click();
    };

    const handleLocaleName = (text: string) => {
      dataSetter([{ key: "localeName", value: text }]);
      if (localeNameError.length)
        dataSetter([{ key: "localeNameError", value: "" }]);
    };

    const handleLanguageName = (text: string) => {
      dataSetter([{ key: "languageName", value: text }]);
    };

    const downloadFile = () => {
      window.open(currentOption[0]?.url, "_blank");
    };

    const onDrop = async (acceptedFiles: any, key: string = "custom") => {
      if (!acceptedFiles.length) {
        dispatch(
          setToastData({
            toastMessage: t("T_NOT_UPLOADED"),
            isToastActive: true,
            toastType: "error",
          })
        );
        return;
      }

      dataSetter([{ key: "file", value: { [key]: acceptedFiles } }]);
    };

    const discardChanges = () => {
      if (ref?.current?.value) {
        ref.current.value = "";
      }
      setData(defaultLanguageSchema);
    };

    const getFileKey = () => {
      return currentOption[0]?.name
        ? currentOption[0].name
        : isValidLocale(localeName)
        ? fileKey
        : "custom";
    };

    return (
      <Card
        title={title}
        icon={Clipboard}
        alt="Clip Icon"
        height=""
        width=""
        className="w-3/4"
      >
        <div className="flex flex-col">
          {!createMode && (
            <div className="flex w-full flex-row justify-between">
              <div className={"w-1/2"}>
                <PickList
                  fieldKey={"name"}
                  fieldValues={["displayName"]}
                  label={pickerTitle}
                  selectedItems={currentOption}
                  onSelectedChange={(item: any) => {
                    let newLang: LanguageObject = cloneDeep(
                      defaultLanguageSchema
                    );
                    newLang.listOptions = listOptions;
                    newLang.currentOption = item;
                    newLang.languageName = getLanguageNameViaOptions(item);
                    setData(newLang);
                  }}
                  items={listOptions}
                  disabled={false}
                  readOnly={false}
                  type={PICKLIST_TYPES.SINGLE}
                />
              </div>
              <div className="ml-2 mr-2 flex flex-row self-end">
                <div className=" ml-2 mr-2 flex">
                  <BiButton
                    className={`flex h-11 flex-row items-center rounded-r-[5px] border border-[#99B0C6] text-primary`}
                    type="button"
                    onClick={() => {
                      dataSetter([
                        { key: "file", value: null },
                        { key: "createMode", value: !createMode },
                      ]);
                    }}
                  >
                    <div className="mr-2 h-4 w-4"> {colouredAddCircle}</div>
                    {addButtonTitle}
                  </BiButton>
                </div>
              </div>
            </div>
          )}
          {(!isEmpty(currentOption) || createMode) && (
            <>
              {!createMode && (
                <div className="mt-5 text-base font-medium">
                  {t("T_CUSTOMIZATION_TYPE_INFO")}
                </div>
              )}
              {createMode && (
                <>
                  <div className={"flex flex-row"}>
                    <div className={"mr-8 w-1/4"}>
                      <InputField
                        type={"text"}
                        id={"languageName"}
                        label={t("T_LANGUAGE_NAME")}
                        placeholder={""}
                        value={languageName}
                        onChange={(val: any) => {
                          handleLanguageName(val);
                        }}
                        disabled={!createMode}
                        error={""}
                        asterixFirst
                        required={createMode}
                        min={1}
                        max={1024}
                      />
                    </div>

                    <div className={"w-1/4"}>
                      <InputField
                        type={"text"}
                        id={"locale"}
                        label={t("T_LOCALE")}
                        placeholder={""}
                        value={localeName}
                        onChange={(val: any) => {
                          handleLocaleName(val);
                        }}
                        disabled={!createMode}
                        error={localeNameError}
                        asterixFirst
                        required={createMode}
                        min={1}
                        max={1024}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="mx-auto w-[75%] py-5">
                <Dropzone
                  onDrop={(file: any) => onDrop(file, getFileKey())}
                  accept={{ "application/json": [".json"] }}
                  maxFiles={1}
                />
                {!isEmpty(file) ? (
                  <FileCard
                    fileName={
                      !isEmpty(data.file)
                        ? data?.file?.[fileKey]?.[0]?.name ||
                          data?.file?.[getFileKey()]?.[0]?.name
                        : ""
                    }
                    discardFile={() => {
                      setData({ ...data, file: null });
                    }}
                  ></FileCard>
                ) : (
                  ""
                )}
              </div>
              <div className="flex flex-row items-center justify-center">
                {!createMode ? (
                  <IconButton
                    className={
                      "!m-0 h-10 w-10 border border-solid border-primary-disabled text-primary"
                    }
                    disabled={isEmpty(currentOption)}
                    onClick={() => {
                      downloadFile();
                    }}
                  >
                    {downloadIcon}
                  </IconButton>
                ) : (
                  ""
                )}
                <BiButton
                  className={"ml-4 bg-primary text-white"}
                  type="button"
                  onClick={handleUpload}
                  disabled={isEmpty(currentOption) && !createMode}
                >
                  {`${t("T_UPLOAD")} ${
                    createMode ? "" : getLanguageName(currentOption[0])
                  }`}
                </BiButton>
                <input
                  type="file"
                  ref={ref}
                  data-testid={"lang-option-ref"}
                  onChange={(e) => {
                    onDrop(e.target.files, getFileKey());
                  }}
                  style={{ display: "none" }}
                  accept={".json"}
                />
              </div>
            </>
          )}
        </div>
      </Card>
    );
  }
);

export default UploadLanguageCard;
